import { reRegistrationStudent } from "../../api/etudiants";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import CommonSelect from "../../core/common/commonSelect";
import { Select } from "antd";
import { Modal } from "react-bootstrap";
import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { getAllClass } from "../../api/pedagogicElement";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { getAllParcours } from "../../api/settings";

//Typage des props parents
type Props = {
  show: boolean;
  metaData: any;
  onClose: () => void;
  onSaved?: () => void;
};

// Shéma pour le formulaire de re-inscription
const re_registrationShema = z.object({
  promotionParcours: z.string().nonempty("Veuillez choisir un parcours valide"),

  promotionYear: z
    .object(
      {
        value: z.string(),
        label: z.string(),
        statut: z.boolean(),
      },
      {
        required_error: "Veuillez choisir une année valide",
        invalid_type_error: "Veuillez choisir une année valide",
      },
    )
    .nullable()
    .refine(
      (val) => val && val.value && val.label,
      "veuillez choisir une année valide",
    ),

  promotionClass: z
    .object(
      {
        value: z.string(),
        label: z.string(),
      },
      {
        required_error: "Veuillez choisir une classe valide",
        invalid_type_error: "Veuillez choisir une classe valide",
      },
    )
    .nullable()
    .refine(
      (val) => val && val.value && val.label,
      "veuillez choisir une classe valide",
    ),
});

type reRegistrationFormFields = z.infer<typeof re_registrationShema>;

const ReregistrationWihtNewParcours = ({
  show,
  metaData,
  onClose,
  onSaved,
}: Props) => {
  const { id } = useParams(); // On récupère l'id de l'URL

  const yearSelectorRef = useRef(null);
  const [allPromotionClass, setAllPromotionClass] = useState<any>([]);
  const [allParcours, setAllParcours] = useState<any[]>([]);

  // iniatialisation de zod avec  react-hook-form pour la gestion du formulaire
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<reRegistrationFormFields>({
    resolver: zodResolver(re_registrationShema),
  });

  const promotionParcours = watch("promotionParcours");

  const promotionYear = watch("promotionYear") as {
    value: string;
    label: string;
    statut: boolean;
  } | null;
  const promotionClass = watch("promotionClass") as {
    value: string;
    label: string;
  } | null;

  // Fonction pour gérer la sélection des années
  const handleYearChange = (
    selectedId: string,
    option?:
      | {
          id: string;
          value: string;
          label: string;
          statut: boolean;
        }
      | {
          id: string;
          value: string;
          label: string;
          statut: boolean;
        }[]
      | undefined,
  ) => {
    // If option is an array (multi-select), pick the first item or handle as needed
    if (Array.isArray(option)) {
      if (option[0]) {
        // Remove 'id' property if present
        const { value, label, statut } = option[0];
        setValue("promotionYear", { value, label, statut });
      } else {
        setValue("promotionYear", null);
      }
    } else if (option) {
      // Remove 'id' property if present
      const { value, label, statut } = option;
      setValue("promotionYear", { value, label, statut });
    } else {
      setValue("promotionYear", null);
    }

    clearErrors();
  };

  // fonction pour charger toute les classes de cette promotion
  const fetchClassData = async () => {
    try {
      if (!promotionYear) return;
      const response = await getAllClass(
        promotionYear.value,
        buildPermissionContext(Resource.STUDENT, Action.UPDATE),
      );

      const matchedParcours = allParcours.find(
        (p: any) => p?.value === promotionParcours,
      );

      const filteredResponse = matchedParcours
        ? response.data.filter((c: any) =>
            matchedParcours?.Cycle?.TypeLevels.some(
              (l: any) => l?.id === c?.Level?.id_typeLevel,
            ),
          )
        : [];

      const formattedClass = filteredResponse
        .slice() // pour éviter de modifier directement la réponse
        .sort((a: any, b: any) => {
          // Tri par Level.label
          const levelCompare = a.Level.label.localeCompare(
            b.Level.label,
            "fr",
            { numeric: true },
          );
          if (levelCompare !== 0) return levelCompare;

          // Si même Level, tri par class label
          return a.label.localeCompare(b.label, "fr", { numeric: true });
        })
        .map((classItem: any) => ({
          value: classItem.id,
          label: `${classItem.Level.label} - ${classItem.label}`,
        }));

      // console.log("All promotions class: ", formattedClass);
      setAllPromotionClass(formattedClass);
      const selectedClassIsValid = formattedClass.some(
        (classe: any) => classe.value === promotionClass?.value,
      );

      if (!selectedClassIsValid) {
        setValue("promotionClass", formattedClass[0] ?? null);
      }
    } catch (error: any) {
      // console.log("erreur get class: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // fonction pour recuperer tous les parcours
  const fetchAllParcours = async () => {
    try {
      const res = await getAllParcours(
        buildPermissionContext(Resource.STUDENT, Action.CREATE),
      );
      // console.log("Response all parcours:", res.data);

      const formattedParcours = (res.data ?? [])
        .map((p: any) => ({
          label: `${p.Cycle.label} ${p.title}`,
          Cycle: p?.Cycle,
          value: p.id,
        }))
        .sort((a: any, b: any) =>
          a.label.localeCompare(b.label, "fr", {
            numeric: true,
            sensitivity: "base",
          }),
        );

      if (formattedParcours.length > 0)
        setValue("promotionParcours", formattedParcours[0].value);
      setAllParcours(formattedParcours);
    } catch (error: any) {
      // console.log("Error lors de la recuperation des parcours : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchAllParcours();
  }, []);

  useEffect(() => {
    fetchClassData();
  }, [promotionYear, promotionParcours, allParcours]);

  const onSubmit: SubmitHandler<reRegistrationFormFields> = async (data) => {
    try {
      //   console.log("form data: ", data);
      if (!id) throw new Error("aucun id trouvé dans l'URL");
      const formData = {
        id_studentProfil: id,
        id_academicYear: data.promotionYear?.value,
        id_class: data.promotionClass?.value,
        id_parcours: data.promotionParcours,
      };
      await reRegistrationStudent(formData);
      toast.success("ré-inscription réussi avec succès");
      reset();
      if (onSaved) onSaved();
      onClose();
    } catch (error: any) {
      // console.log("erreur re-registration: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  return (
    <>
      {/* /Archives */}
      <Modal
        show={show}
        onHide={onClose}
        centered
        // size="undefined"
        id="add-parent-for-student"
      >
        <div className="modal-header">
          <h4 className="modal-title">Ajouter une inscription</h4>
          <button
            type="button"
            className="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={onClose}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="modal-body pb-0">
          {/* Selection des parcours univaisitaires */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div
              ref={yearSelectorRef}
              className="mb-12 flex-fill "
              id="year-selector"
            >
              <label htmlFor="" className="form-label">
                Selectionné un Parcours universitaire
              </label>
              <br />
              <Select
                id="parcours-selector"
                // mode="multiple"
                //   allowClear
                className={`select ${errors.promotionParcours ? "is-invalid" : ""}`}
                getPopupContainer={() =>
                  yearSelectorRef.current || document.body
                }
                style={{
                  maxWidth: "500px",
                  width: "100%",
                  color: "white",
                }}
                placeholder="Selectionner un parcours"
                defaultValue={undefined}
                value={promotionParcours ?? undefined}
                options={allParcours}
                onChange={(option) => {
                  // console.log("selected Parcours: ", option);
                  if (option) setValue("promotionParcours", option);
                  clearErrors("promotionParcours");
                }}
                // Permet de chercher par label a lieu de l'id
                filterOption={(input: any, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              {errors.promotionParcours && (
                <div className="invalid-feedback">
                  {errors.promotionParcours?.message}
                </div>
              )}

              {/* Selection des années */}
              <label htmlFor="" className="form-label">
                Selectionné une Année universitaire
              </label>
              <br />
              <Select
                id="year-selector"
                // mode="multiple"
                //   allowClear
                className={`select ${errors.promotionYear ? "is-invalid" : ""}`}
                getPopupContainer={() =>
                  yearSelectorRef.current || document.body
                }
                style={{
                  maxWidth: "500px",
                  width: "100%",
                  color: "white",
                }}
                placeholder="Selectionne une annee academique"
                value={promotionYear?.value ?? undefined}
                options={
                  metaData?.academicYears
                    ? metaData?.academicYears.filter(
                        (y: any) =>
                          !metaData?.studentArchive?.some(
                            (archiveItem: any) =>
                              archiveItem.id_academicYear === y.value,
                          ) &&
                          !metaData?.studentExternalNotes.some(
                            (note: any) => note.id_academicYear === y.value,
                          ),
                      )
                    : (metaData?.academicYears ?? [])
                }
                onChange={handleYearChange}
                // Permet de chercher par label a lieu de l'id
                filterOption={(input: any, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              {errors.promotionYear && (
                <div className="invalid-feedback">
                  {errors.promotionYear?.message}
                </div>
              )}
              {/* Selection des classes */}
              {promotionYear && (
                <div className="mt-2">
                  <h6>Sélectionné une classe:</h6>
                  <div className=" justify-content-between align-items-center">
                    {/* <span>Licence 01 MOP</span> */}
                    <CommonSelect
                      className={`select ${
                        errors.promotionClass ? "is-invalid" : ""
                      }`}
                      options={allPromotionClass}
                      defaultValue={undefined}
                      value={promotionClass ?? undefined}
                      onChange={(option: any) => {
                        if (option) setValue("promotionClass", option);
                        else setValue("promotionClass", null);
                        clearErrors("promotionClass");
                      }}
                    />
                    {errors.promotionClass && (
                      <div className="invalid-feedback">
                        {errors.promotionClass?.message}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="mt-4 mb-3 d-flex justify-content-end mr-8">
                <Link
                  to="#"
                  id="cancel-btn"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={() => {
                    setValue("promotionClass", null);
                    setValue("promotionYear", null);
                    onClose();
                  }}
                >
                  Annuler
                </Link>
                <button type="submit" className="btn btn-outline-primary ">
                  Valider
                </button>
              </div>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default ReregistrationWihtNewParcours;
